/**
 * Nova Derma — the one navigation registry (DS-05).
 *
 * Every screen the sidebar can reach is declared here once: its route, its
 * Arabic label, its icon, the permission that gates it (SEC-01) and, where the
 * screen represents a queue of work, the badge counter that reports its depth.
 * The router is wired to these exact paths — changing a `to` here without
 * changing the route registration silently breaks the link.
 *
 * `permission` is read with `canAny`: a single key means "needs this", an array
 * means "needs at least one of these". Omitting it makes the item unconditional,
 * which today only the landing dashboard is.
 */

import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  BellRing,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Coins,
  FileText,
  FlaskConical,
  FolderTree,
  Landmark,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  Package,
  Pill,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Stethoscope,
  Syringe,
  TrendingUp,
  Users,
} from 'lucide-react';
import { PERMISSIONS } from '@nova/shared';
import type { PermissionKey } from '@nova/shared';

export interface NavItem {
  /** Stable react key and the handle other chrome (shortcuts, breadcrumbs) refers to. */
  key: string;
  labelAr: string;
  labelEn: string;
  /** Absolute route path, exactly as registered in `routes.tsx`. */
  to: string;
  icon: LucideIcon;
  /** SEC-01 — one key, or several of which any one is enough. Absent = always visible. */
  permission?: PermissionKey | PermissionKey[];
  /** SEC-01 — every listed key is required; used by specialty pack administration. */
  allPermissions?: PermissionKey[];
  /** Field of `GET /notifications/counts` to render as the item's badge. */
  countKey?: string;
}

export interface NavGroup {
  key: string;
  labelAr: string;
  labelEn: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'daily',
    labelAr: 'التشغيل اليومي',
    labelEn: 'Daily operations',
    items: [
      {
        key: 'dashboard',
        labelAr: 'الرئيسية',
        labelEn: 'Dashboard',
        to: '/dashboard',
        icon: LayoutDashboard,
        // RPT-02 — the landing screen adapts to the role rather than being hidden
        // from it, so every authenticated user keeps a place to land.
      },
      {
        key: 'calendar',
        labelAr: 'الكالندر',
        labelEn: 'Calendar',
        to: '/calendar',
        icon: CalendarDays,
        permission: PERMISSIONS.APPOINTMENT_VIEW,
      },
      {
        key: 'online-bookings',
        labelAr: 'طلبات الحجز عبر الإنترنت',
        labelEn: 'Online bookings',
        to: '/online-bookings',
        icon: ClipboardCheck,
        permission: PERMISSIONS.ONLINE_BOOKING_VIEW,
        countKey: 'onlineBookingPending',
      },
      {
        key: 'queue',
        labelAr: 'مواعيد اليوم',
        labelEn: "Today's queue",
        to: '/queue',
        icon: ListChecks,
        permission: PERMISSIONS.DOCTOR_QUEUE_VIEW,
        countKey: 'waitingPatients',
      },
      {
        key: 'appointment-closure',
        labelAr: 'مواعيد بحاجة إغلاق',
        labelEn: 'Needs closure',
        to: '/appointments/closure',
        icon: ClipboardCheck,
        // APT-08/09 — an appointment left open is an uninvoiced session.
        permission: PERMISSIONS.APPOINTMENT_UPDATE,
        countKey: 'needsClosure',
      },
    ],
  },

  {
    key: 'patients',
    labelAr: 'المرضى',
    labelEn: 'Patients',
    items: [
      {
        key: 'patient-files',
        labelAr: 'ملفات المرضى',
        labelEn: 'Patient files',
        to: '/patients',
        icon: Users,
        permission: PERMISSIONS.PATIENT_VIEW,
      },
      {
        key: 'recalls',
        labelAr: 'متابعات',
        labelEn: 'Recalls',
        to: '/patients/recalls',
        icon: BellRing,
        permission: PERMISSIONS.RECALL_MANAGE,
        countKey: 'openRecalls',
      },
    ],
  },

  {
    key: 'clinical',
    labelAr: 'السريري',
    labelEn: 'Clinical',
    items: [
      {
        key: 'visits',
        labelAr: 'الزيارات',
        labelEn: 'Visits',
        to: '/clinical/visits',
        icon: Stethoscope,
        permission: PERMISSIONS.VISIT_VIEW,
      },
      {
        key: 'prescriptions',
        labelAr: 'الوصفات',
        labelEn: 'Prescriptions',
        to: '/pharmacy/prescriptions',
        icon: Pill,
        permission: PERMISSIONS.PRESCRIPTION_VIEW,
      },
      {
        key: 'labs',
        labelAr: 'الفحوصات',
        labelEn: 'Labs',
        to: '/labs',
        icon: FlaskConical,
        permission: PERMISSIONS.LAB_ORDER_VIEW,
      },
    ],
  },

  {
    key: 'inventory',
    labelAr: 'المخزون',
    labelEn: 'Inventory',
    items: [
      {
        key: 'items',
        labelAr: 'الأصناف',
        labelEn: 'Items',
        to: '/inventory/items',
        icon: Package,
        permission: PERMISSIONS.ITEM_VIEW,
      },
      {
        key: 'inventory-categories',
        labelAr: 'التصنيفات',
        labelEn: 'Categories',
        to: '/inventory/categories',
        icon: FolderTree,
        permission: PERMISSIONS.CATEGORY_VIEW,
      },
      {
        key: 'purchasing',
        labelAr: 'فواتير الشراء',
        labelEn: 'Purchase invoices',
        to: '/purchasing',
        icon: ShoppingCart,
        permission: PERMISSIONS.PURCHASE_VIEW,
      },
      {
        key: 'stock-movements',
        labelAr: 'حركات المخزون',
        labelEn: 'Stock movements',
        to: '/stock/movements',
        icon: ArrowLeftRight,
        permission: PERMISSIONS.STOCK_MOVEMENT_VIEW,
      },
      {
        key: 'stocktake',
        labelAr: 'الجرد',
        labelEn: 'Stocktake',
        to: '/stock/stocktake',
        icon: ClipboardList,
        permission: [PERMISSIONS.STOCKTAKE_PERFORM, PERMISSIONS.STOCKTAKE_REPORT_VIEW],
      },
      {
        key: 'stock-alerts',
        labelAr: 'تنبيهات المخزون',
        labelEn: 'Stock alerts',
        to: '/stock/alerts',
        icon: AlertTriangle,
        // INV-101 — expiry, reorder point and dormant stock all surface here.
        permission: [PERMISSIONS.ITEM_VIEW, PERMISSIONS.STOCK_MOVEMENT_VIEW],
        countKey: 'stockAlerts',
      },
    ],
  },

  {
    key: 'finance',
    labelAr: 'المالية',
    labelEn: 'Finance',
    items: [
      {
        key: 'invoices',
        labelAr: 'الفواتير',
        labelEn: 'Invoices',
        to: '/billing/invoices',
        icon: FileText,
        permission: PERMISSIONS.INVOICE_VIEW,
      },
      {
        key: 'jofotara',
        labelAr: 'إدارة جوفوترة',
        labelEn: 'JoFotara',
        to: '/billing/jofotara',
        icon: Landmark,
        permission: PERMISSIONS.INVOICE_VIEW,
      },
      {
        key: 'insurance-companies',
        labelAr: 'شركات التأمين',
        labelEn: 'Insurance companies',
        to: '/billing/insurance',
        icon: ShieldCheck,
        permission: PERMISSIONS.INSURANCE_VIEW,
      },
      {
        key: 'commission-statements',
        labelAr: 'كشوفات العمولات',
        labelEn: 'Commission statements',
        to: '/commissions',
        icon: Receipt,
        // COST-16 — a provider reaches the same screen scoped to their own statement.
        permission: [
          PERMISSIONS.COMMISSION_STATEMENT_VIEW_OWN,
          PERMISSIONS.COMMISSION_STATEMENT_VIEW_ALL,
        ],
      },
      {
        key: 'expenses',
        labelAr: 'المصاريف التشغيلية',
        labelEn: 'Operating expenses',
        to: '/expenses',
        icon: Coins,
        permission: PERMISSIONS.EXPENSE_VIEW,
      },
      {
        key: 'pnl',
        labelAr: 'الأرباح والخسائر',
        labelEn: 'Profit & loss',
        to: '/reports/pnl',
        icon: TrendingUp,
        permission: PERMISSIONS.PNL_VIEW,
      },
    ],
  },

  {
    key: 'setup',
    labelAr: 'الإعدادات',
    labelEn: 'Setup',
    items: [
      {
        key: 'providers',
        labelAr: 'مقدمو الخدمة',
        labelEn: 'Service providers',
        to: '/providers',
        icon: Stethoscope,
        // PROV-01 — first among the setup screens because nothing else in daily
        // operations works until it has rows: an appointment needs a provider.
        permission: PERMISSIONS.PROVIDER_VIEW,
      },
      {
        key: 'procedures',
        labelAr: 'الإجراءات',
        labelEn: 'Procedures',
        to: '/catalog/procedures',
        icon: Syringe,
        permission: PERMISSIONS.PROCEDURE_VIEW,
      },
      {
        key: 'packages',
        labelAr: 'الباقات',
        labelEn: 'Packages',
        to: '/catalog/packages',
        icon: Package,
        // A bundle of procedures sold at one price ("باقة إزالة الشعر بالليزر").
        permission: PERMISSIONS.PACKAGE_VIEW,
      },
      {
        key: 'campaigns',
        labelAr: 'الحملات',
        labelEn: 'Campaigns',
        to: '/messaging/campaigns',
        icon: Megaphone,
        permission: PERMISSIONS.CAMPAIGN_MANAGE,
      },
      {
        key: 'settings',
        labelAr: 'الإعدادات العامة',
        labelEn: 'General settings',
        to: '/settings',
        icon: Settings,
        permission: PERMISSIONS.SETTINGS_MANAGE,
      },
      {
        key: 'users',
        labelAr: 'المستخدمون والصلاحيات',
        labelEn: 'Users & permissions',
        to: '/users',
        icon: ShieldCheck,
        permission: [PERMISSIONS.USER_VIEW, PERMISSIONS.USER_MANAGE, PERMISSIONS.ROLE_MANAGE],
      },
    ],
  },

  {
    key: 'reports',
    labelAr: 'التقارير',
    labelEn: 'Reports',
    items: [
      {
        key: 'reports',
        labelAr: 'التقارير',
        labelEn: 'Reports',
        to: '/reports',
        icon: BarChart3,
        // RPT-* — the hub lists only the report families the user may open.
        permission: [
          PERMISSIONS.REPORT_FINANCIAL,
          PERMISSIONS.REPORT_OPERATIONAL,
          PERMISSIONS.REPORT_PATIENTS,
          PERMISSIONS.REPORT_CLINICAL,
          PERMISSIONS.REPORT_INVENTORY,
          PERMISSIONS.REPORT_COMMISSION,
        ],
      },
    ],
  },
];
